import { Button, Dropdown, Switch } from 'antd';
import Barchart from 'components/molecules/Barchart';
// import { growthChartData } from '../Growthdata';
import { useContext, useMemo, useRef, useState } from 'react';
import { calculateContainerHeight, formatNumWithKMBAlt, getConfig, scaleMaxValue } from 'utils/helperMethods';
import useProfitGrowthByHierarchy from './hooks/useProfitGrowthByHierarchy';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { groupBarChartWidth } from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import { isEmpty, startCase } from 'lodash';
import { useProfitPoolOverviewContext } from './context/ProfitPoolOverviewContext';
import { UserContext } from 'contexts/ConfigContext';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import CustomDropdownButton from 'components/atoms/CustomDropdownButton';
import DownSvg from 'assets/svg/Down';
import { PP_OVERVIEW_SORTED_BY_GROWTH } from 'utils/mixpanelEventNames';
import { trackEventMixpanel } from 'utils/mixpanel';

const ProfitGrowthByHierarchyChart = () => {
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { ppGrowthDropDownOptions, kpiLabelMapping, defaultCurrencySymbol } = profitPoolConfig;
    const chartHeight = calculateContainerHeight(3);
    const { selectedSubFilter, selectPPGrowthUnit, setSelectPPGrowthUnit } = useProfitPoolOverviewContext();
    // const [growthData, setGrowthData] = useState(growthChartData);
    const [isAscending, setIsAscending] = useState(false);
    const [toLeft, setToLeft] = useState(0);
    const chartRef = useRef<any>(null);
    const containerWidth2 = useContainerWidth({
        containerId: 'profit-growth-by-hierarchy-chart-infinite-scroll',
    });
    const graphViewContainerWidth3 = Math.max(16 * groupBarChartWidth, containerWidth2);

    const ppGrowthDropDownOptionsWithCurrency = useMemo(() => {
        return ppGrowthDropDownOptions.map((option: any) => {
            return {
                label: `${option.label.replace('$', defaultCurrencySymbol)}`,
                key: option.key,
            };
        });
    }, [ppGrowthDropDownOptions, defaultCurrencySymbol]);

    const {
        data: profitGrowthData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        min,
        max,
    } = useProfitGrowthByHierarchy();

    const maxBound = useMemo(() => {
        return max > 0 ? max : 0;
    }, [max]);

    const sortedProfitGrowthData = useMemo(() => {
        if (!profitGrowthData || profitGrowthData.length === 0) {
            return profitGrowthData;
        }
        const sorted = [...profitGrowthData].sort((a, b) => {
            if (isAscending) {
                return a.profit_growth - b.profit_growth;
            } else {
                return b.profit_growth - a.profit_growth;
            }
        });
        return sorted;
    }, [profitGrowthData, isAscending]);

    // Prepare Excel export data
    const excelExportData = useMemo(() => {
        if (!sortedProfitGrowthData?.length) {
            return { headers: [], data: [] };
        }

        const selectedUnitLabel =
            ppGrowthDropDownOptionsWithCurrency.find((option: any) => option.key === selectPPGrowthUnit)?.label ||
            kpiLabelMapping['profit'] ||
            'Profit Growth';

        const headers = [startCase(selectedSubFilter?.dataKey || 'Attribute'), selectedUnitLabel];

        const data = sortedProfitGrowthData.map((item: any) => [item.attributeDesc || '', item.profit_growth || 0]);

        return { headers, data };
    }, [
        sortedProfitGrowthData,
        selectedSubFilter,
        selectPPGrowthUnit,
        ppGrowthDropDownOptionsWithCurrency,
        kpiLabelMapping,
    ]);

    if (isPending) {
        return (
            <div>
                <div className="text-lg font-semibold">
                    <p>{`${kpiLabelMapping['profit']} Growth by ${startCase(selectedSubFilter?.dataKey)}`}</p>
                </div>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </div>
        );
    }
    if (isError) {
        return (
            <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ErrorBox />
            </div>
        );
    }

    if (isEmpty(sortedProfitGrowthData)) {
        return (
            <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ErrorBox msg={'No Data'} />
            </div>
        );
    }
    return (
        <section>
            {/* <div className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 py-2 w-full"> */}
            <div>
                <div className="flex flex-row justify-between">
                    <div className="text-lg font-semibold">
                        <p>{`${kpiLabelMapping['profit']} Growth by ${startCase(selectedSubFilter?.dataKey)}`}</p>
                    </div>
                    <div className="flex-row flex gap-x-2">
                        <div className="flex flex-row gap-1 items-center">
                            <p>{'Asc'}</p>
                            <Switch
                                defaultChecked
                                onChange={() => {
                                    trackEventMixpanel(PP_OVERVIEW_SORTED_BY_GROWTH, {
                                        sorted_by: isAscending ? 'desc' : 'asc',
                                    });
                                    setIsAscending(!isAscending);
                                }}
                            />
                            <p>{'Desc'}</p>
                        </div>
                        <div>
                            <Dropdown
                                menu={{
                                    items: ppGrowthDropDownOptionsWithCurrency,
                                    onClick: (info) => {
                                        setSelectPPGrowthUnit(info.key);
                                    },
                                }}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <CustomDropdownButton type="default">
                                    {
                                        ppGrowthDropDownOptionsWithCurrency.find(
                                            (option: any) => option.key === selectPPGrowthUnit,
                                        )?.label
                                    }
                                    <div className="ml-2">
                                        <DownSvg />
                                    </div>
                                </CustomDropdownButton>
                            </Dropdown>
                        </div>
                        <div className="flex flex-row gap-x-2 items-center">
                            <CopyToClipBoard refOfComponent={chartRef} />
                            <DownloadExcel
                                title={`${kpiLabelMapping['profit']} Growth by ${startCase(
                                    selectedSubFilter?.dataKey,
                                )}`}
                                excelData={excelExportData.data}
                                headers={excelExportData.headers}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex flex-row justify-between">
                    <div>
                        <p className="text-base">{`Total ${kpiLabelMapping['profit']} ${defaultCurrencySymbol}`}</p>
                    </div>
                    <div className="flex flex-row gap-2 text-xs items-center">
                        <div className="bg-[#42A531] w-3 h-2 self-center" />
                        <p>{'Positive Growth'}</p>
                        <div className="bg-[#FF4D4F] w-3 h-2 self-center" />
                        <p>{'Negative Growth'}</p>
                    </div>
                    <div />
                </div>
                <div>
                    <HorizontalInfiniteScroller
                        id={`profit-growth-by-hierarchy-chart-infinite-scroll`}
                        hasMore={hasNextPage}
                        scrollThreshold={0.9}
                        toScrollWidth={toLeft}
                        onNext={(width: number) => {
                            !isFetchingNextPage && fetchNextPage();
                            // width > 0 && setToLeft(toLeft + width);
                        }}
                    >
                        <div
                            id="profit-growth-by-hierarchy-chart-infinite-scroll"
                            ref={chartRef}
                            className="mt-10 overflow-x-auto max-w-full pb-[60px]"
                        >
                            <Barchart
                                padding={[0, 0, 0, 0]}
                                barWidth={40}
                                xAxisConfig={true}
                                xAxisLabelFormatter={(val: any) => {
                                    return val;
                                }}
                                xLabelFontSize={12}
                                dataset={sortedProfitGrowthData}
                                height={chartHeight + 50}
                                width={Math.max(
                                    graphViewContainerWidth3 * (sortedProfitGrowthData?.length / 10 || 1),
                                    graphViewContainerWidth3,
                                )}
                                loading={isFetchingNextPage}
                                chartContainer={`profit-growth-by-hierarchy-chart`}
                                fields={['attributeDesc', 'profit_growth', 'trend']}
                                minBound={min}
                                maxBound={scaleMaxValue(20, maxBound)}
                                colorKey={'trend'}
                                YGrid={false}
                                yAxisConfig={true}
                                yAxisLabelFormatter={(val: any) => {
                                    return `${defaultCurrencySymbol} ${formatNumWithKMBAlt(val)}`;
                                }}
                                showLoader={false}
                                tooltipFormatter={(xAxis: any, val: any) => {
                                    return {
                                        name: `${kpiLabelMapping['profit']} Growth`,
                                        value: `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`,
                                    };
                                }}
                            />
                        </div>
                    </HorizontalInfiniteScroller>
                </div>
            </div>
        </section>
    );
};

export default ProfitGrowthByHierarchyChart;
